import { Stack, Typography } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import AaFamilyClaim, { buildClaim } from '@/james-playground/aa-family-claim/AaFamilyClaim';
import AdviserView from '@/james-playground/aa-family-claim/AdviserView';
import { aaScenes, sent } from '@/james-playground/aa-family-claim/AaFamilyClaim.scenes';
import {
  AREAS,
  ASKS,
  BATCH_GATES,
  EFFORT,
  OWNER,
  VOICE_THRESHOLD,
  say,
} from '@/james-playground/aa-family-claim/claim/asks';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';
import { ENTRIES, isAsked, isFormOnly, type Condition } from '@/james-playground/aa-family-claim/claim/registry';
import { type ScenarioId } from '@/james-playground/aa-family-claim/claim/scenarios';

const TODAY = new Date(2026, 9, 1);
const V = { name: 'Margaret', selfClaim: false };

const sceneOf = (id: string) => {
  const found = aaScenes.find((s) => s.id === id);
  if (!found) throw new Error(`Unknown scene: ${id}`);
  return found;
};

const at = (id: string, scenario: ScenarioId = 'standard') => () => (
  <AaFamilyClaim key={`${id}-${scenario}`} today={TODAY} initial={{ ...sceneOf(id).initial, scenario }} />
);

const step = (id: string, title: string, tap?: string, note?: string, scenario: ScenarioId = 'standard') => ({
  id: `${scenario}-${id}`,
  title,
  tap,
  render: at(id, scenario),
  note: note ? <Note variant='body2'>{note}</Note> : undefined,
});

// ---- Logic tables, generated from the registry and the asks --------------

const REQUIREDNESS: Record<string, string> = {
  required_to_process: 'Ready to send',
  needed_for_decision: 'Ready to send',
  strengthens_claim: 'Make it stronger',
  optional: 'Neither',
};

/** A condition in words, using the same short names the screens use. */
const describe = (c?: Condition): string => {
  if (!c) return 'Always';
  if ('all' in c) return c.all.map(describe).join(' and ');
  if ('any' in c) return `any of: ${c.any.map(describe).join(', ')}`;
  if ('fn' in c) return 'a Welsh postcode';
  const name = labelOf(c.id, V);
  switch (c.op) {
    case 'eq':
      return `${name} is ${c.value}`;
    case 'ne':
      return `${name} is not ${c.value}`;
    case 'has':
      return `${name} includes ${c.value}`;
    case 'filled':
      return c.perRow ? 'that row is filled' : `${name} is answered`;
    case 'empty':
      return `${name} is empty`;
    case 'in':
      return `${name} is an EEA or Swiss nationality`;
  }
};

const registryGroups = AREAS.map((area) => ({
  label: say(area.label, V),
  rows: ENTRIES.filter((e) => isAsked(e) && OWNER[e.id]?.area === area.id).map((e) => [
    e.q ?? 'Product',
    labelOf(e.id, V),
    e.answerType.replace(/_/g, ' '),
    describe(e.when),
    REQUIREDNESS[e.requiredness],
    OWNER[e.id].id,
  ]),
})).filter((g) => g.rows.length);

const askRows = ASKS.map((a) => [
  a.id,
  say(a.area === 'start' ? 'Before we start' : AREAS.find((x) => x.id === a.area)?.label ?? '', V),
  a.kind,
  String(EFFORT[a.kind]),
  String(a.entries.length),
  a.gate ? labelOf(a.gate, V) : '',
]);

const formOnly = ENTRIES.filter(isFormOnly).map((e) => e.q ?? e.id).join(', ');

/**
 * The family-led Attendance Allowance claim as an engineering spec: every
 * pathway walked from the live prototype, and every rule the screens run on,
 * generated from the same registry and asks the prototype uses.
 */
export const familyAaClaimSpec: HandoverSpec = {
  id: 'family-aa-claim',
  title: 'Family AA claim',
  description:
    'A family member prepares an Attendance Allowance claim with their Care Adviser. The claim comes to them with what KareHero already knows, shows what is still needed, and asks for it the quickest way, round after round, until the AA1 is complete.',
  links: [{ label: 'Open the prototype', url: '/sandbox/family-aa-claim' }],
  readme: (
    <Note title='Readme' variant='body2'>
      <Stack spacing={1.5}>
        <Typography variant='body2'>
          The loop: come with what we have, say what is still needed, offer the quickest ways to give it (a voice note, a
          photo, a few taps), add the answers to the claim, and repeat. The hub sits between rounds. Each round is a short
          chat for one step.
        </Typography>
        <Typography variant='body2'>
          The logic is deterministic and lives in three files engineering can lift: <code>claim/aa1-registry.json</code>{' '}
          (every AA1 question, sub-part, widget and printed routing rule), <code>claim/asks.ts</code> (how the form is
          grouped into areas and asks) and <code>claim/engine.ts</code> (applicability, the two bars, ranking, routing).
          The tables below are generated from them.
        </Typography>
        <Typography variant='body2'>
          Simulated here: voice notes are scripted per scenario, document photos are fixtures, a typed account is matched to
          topics by keyword, and Send stops at the sent screen. Real: every rule, every condition, and the AA1 11/25 PDF,
          filled only from confirmed answers, signatures blank.
        </Typography>
        <Typography variant='body2'>
          Five scenarios run the same screens with different people: Margaret at home, Patricia on the quicker route,
          Joan in a council-funded care home, Rose claiming for herself, and Eileen, whose son holds Power of Attorney.
          Switch them on the prototype&apos;s stage.
        </Typography>
      </Stack>
    </Note>
  ),
  tables: [
    {
      title: 'Routing: the decision order',
      caption: 'Tested in this order after every answer that could change it. The first that routes away ends the AA1 journey with a handoff screen.',
      columns: ['Order', 'Test', 'Decided by', 'Outcome'],
      rows: [
        ['1', 'Where she lives', 'Postcode (production: postcode lookup)', 'Scotland: PADP handoff. BT postcode: Northern Ireland handoff.'],
        ['2', 'Normally lives in Great Britain', 'Q9', 'No: lives-abroad handoff.'],
        ['3', 'State Pension age', 'Date of birth against the official timetable', 'Under: PIP handoff, with the date she reaches it.'],
        ['4', 'Existing disability benefits', 'Before we start', 'AA now: renewal or change. PIP, DLA or ADP now: she keeps it. PIP or ADP ended in 12 months: new PIP claim.'],
        ['5', 'Who signs', 'Before we start, then Q15', 'Shapes signer screens and Q15.'],
        ['6', 'The quiet end-of-life line', 'Before we start, Q1', 'Yes: the quicker route.'],
        ['7', 'Nationality and immigration', 'Q8', 'EEA or Swiss: Q8.2. Irish and British are not asked.'],
        ['8', 'Time abroad', 'Q10', 'Yes: the abroad details.'],
        ['9', 'Hospital, hospice or care home', 'Q47, Q48', 'Payment warning before sending; the claim stays sendable.'],
        ['10', 'When care needs began', 'Q28', 'Under 6 months: the date payment could start. Never blocks.'],
      ],
    },
    {
      title: 'The gather loop',
      caption: 'The rules behind the hub and every round. All of them are in claim/engine.ts.',
      columns: ['Concept', 'Rule'],
      rows: [
        ['Applies', 'An entry applies when its condition is true. A condition that refers to an unanswered question is pending: not counted, not asked. A follow-up whose deciding question does not apply does not apply either.'],
        ['Statuses', 'not applicable, pending, open, proposed, conflict, confirmed, skipped. Only an entry with no fact is open.'],
        ['Ready to send', 'No open entry that is required to process or needed for the decision. Proposed answers are confirmed at the Check step; skipped answers go to the adviser.'],
        ['Make it stronger', 'After ready: the open entries that strengthen the claim (the words on the form, professionals, helpers, the supporting statement, Q63).'],
        ['Area remainder', 'Each started area shows its open asks ("2 to go"). An ask is one prompt, however many boxes it fills. There is never a grand total.'],
        ['Ranking', 'Score = entries it clears, weighted 3 (required to process), 2 (needed for decision), 1 (strengthens), divided by effort. Before ready, only bar 1 entries count. Ties follow hub order.'],
        ['Effort', 'One tap 1, batch check 2, amount or date 2, typed field 3, grid 3, table 4, photo 4, voice prompt 6.'],
        ['Opening voice', 'Always the first step until done. Declining AI reading marks it done and the hub offers questions only.'],
        ['Second-pass voice', `Offered for an area with at least ${VOICE_THRESHOLD} open entries that a voice note can answer, once per area.`],
        ['Documents', 'Prescription while conditions and medicines are open. Hospital or clinic letter while professionals, tests, surgery or a stay are Yes or unknown. Care plan when a paid carer or agency is named.'],
        ['Batch check', `After the first voice round, the open gates of ${BATCH_GATES.length} everyday activities. Ticked: Yes, then that activity's card. Unticked: confirmed "manages on her own", source batch check.`],
        ['Good and bad days', 'Asked once per care area, straight after its details, when the area is Yes. Feeds the words on the form and Q46.'],
        ['Skip', '"I’m not sure, we’ll talk it through". Counts toward ready to send and lands on the adviser’s list. A skipped gate decides nothing, so its follow-ups stay pending.'],
        ['Proposals', 'From voice, typing, documents and earlier calls. A family answer is never overwritten. A disagreeing source makes a conflict, settled by the family at Check.'],
      ],
    },
    {
      title: 'Pathways',
      caption: 'Flags that combine: an attorney can sign a quicker-route claim for someone in a hospice.',
      columns: ['Pathway', 'Set by', 'What changes'],
      rows: [
        ['Quicker route (special rules)', 'Yes to the quiet line, Q1', 'Q21 to Q46 drop and the care areas leave the hub. An SR1 task appears and never blocks. The words "end of life" and "special rules" never appear again. The qualifying-period warning is off. The helper can sign under the special-rules option.'],
        ['Hospital, hospice or care home', 'Q47 or Q48 Yes, with the place type', 'Stay details and the funding question. The payment consequence is shown before sending. Uncertain funding goes to the adviser.'],
        ['She claims herself', 'The account holder is the claimant', 'Every prompt in the second person. No signer step; Q15 is No.'],
        ['Someone with authority signs', 'Who signs, then Q15', 'Registered Property and Financial Affairs LPA: attorney, with the online-account question. Health and Welfare or unregistered: cannot sign, explained, continues as not sure. Deputy, appointee, Scottish categories, organisations, and applying to be appointed.'],
      ],
    },
    {
      title: 'Asks',
      caption: 'Every ask, the area it sits in, its answer card, its effort, how many registry entries it owns, and the gate that opens its detail.',
      columns: ['Ask', 'Area', 'Card', 'Effort', 'Entries', 'Gate'],
      rows: askRows,
    },
    {
      title: 'The AA1, question by question',
      caption: `Every entry the product asks about, grouped by area: its question, what it is called on screen, its answer type, when it is asked, which bar it counts toward, and the ask that owns it. Left blank for the signer: ${formOnly}.`,
      columns: ['Question', 'On screen', 'Answer', 'Asked when', 'Counts toward', 'Ask'],
      groups: registryGroups,
    },
    {
      title: 'The PDF',
      caption: 'claim/pdf.ts fills the official AA1 11/25 from confirmed answers through the registry.',
      columns: ['Rule', 'Detail'],
      rows: [
        ['Only confirmed answers', 'Proposed, conflicting and skipped answers leave their boxes blank.'],
        ['Choices', 'Each option carries its widget and export value. Radio groups take the export value; separate checkboxes are ticked.'],
        ['Tables', 'Row n of every column goes to that column’s nth widget, for every row the form provides.'],
        ['Comb fields', 'NI number, sort code and account number keep their character limit; digits only.'],
        ['Overflow', 'An answer longer than its box continues in Q63 under its question number. Nothing is truncated.'],
        ['Blank by design', 'Signatures, signature dates, the page 31 checklist and the worked-example box.'],
      ],
    },
    {
      title: 'What production needs',
      caption: 'The prototype does not establish real-model accuracy, eligibility or DWP acceptance.',
      columns: ['Area', 'In this prototype', 'For production'],
      rows: [
        ['Capture and extraction', 'Scripted voice notes and document fixtures per scenario.', 'Live speech and document extraction proposing registry ids with quotes, inside these rules.'],
        ['Routing', 'Postcode areas and the State Pension age timetable in code.', 'A postcode lookup, and the existing-award check from KareHero records.'],
        ['Saving', 'Answers live in the open page.', 'Authenticated claim storage, resume and history.'],
        ['Adviser', 'The adviser view shows the sent claim.', 'Delivery, assignment, answering the "To talk through" list, and returning answers to the family to confirm.'],
        ['Submission', 'Print, sign and post, as a swappable last step.', 'Decide the route: print and post, adviser posts, DWP online handoff, or a mailed pre-filled form.'],
      ],
    },
  ],
  flows: [
    {
      title: 'Margaret at home: the standard claim',
      note: (
        <Note title='Readme' variant='body2'>
          Sarah claims for her mum. Before we start confirms what KareHero holds and settles who signs. The opening voice note
          fills most of the care answers, including a night answer that disagrees with her care profile. The hub then ranks
          what is left: a photo of her prescription, a batch check of the activities she never mentioned, and the cards for
          the rest.
        </Note>
      ),
      steps: [
        step('welcome', 'Welcome', 'Start'),
        step('before', 'What we have', 'Yes, that’s right', 'Profile facts are proposals: a tap confirms them. Routing runs after every answer.'),
        step('before-quiet', 'The quiet line', 'No', 'Asked of everyone, once, in neutral words.'),
        step('hub-new', 'Start with a voice note', 'Tell me about a typical day', 'The opening voice note is always the first step.'),
        step('round-recording', 'Recording', 'Send recording', 'Hub areas light up as they are heard.'),
        step('hub', 'After the voice note', 'Photo of her prescription', 'Call notes from her Care Adviser arrive as proposals under "Since you were last here".'),
        step('round-batch', 'The batch check', 'Continue', 'Unticked activities are confirmed as managed on her own.'),
        step('card-grid', 'An activity card', 'Continue', 'Difficulty, hands-on help and reminding, with times a day, exactly as the form asks.'),
        step('hub-ready', 'Ready to send', 'Check and send'),
        step('check', 'Check the answers', 'They’re all right', 'Every proposal and conflict, with its source and its question number.'),
        step('send', 'Send to your Care Adviser', 'Send to my Care Adviser', 'Everything set aside is listed to talk through.'),
        step('sent', 'Sent'),
      ],
    },
    {
      title: 'Patricia: the quicker route',
      steps: [
        step('before-quiet', 'The quiet line', 'Yes', 'The only time these words appear.', 'endOfLife'),
        step('hub-new', 'A shorter claim', 'Tell me about her health', 'The care areas are gone; the SR1 task never blocks.', 'endOfLife'),
        step('hub', 'After the voice note', undefined, undefined, 'endOfLife'),
        step('send', 'Send', undefined, undefined, 'endOfLife'),
      ],
    },
    {
      title: 'Joan: in a care home',
      steps: [
        step('hub', 'After the voice note', 'Staying somewhere now', 'The voice note proposes the stay and its funding.', 'careHome'),
        step('card-stay', 'The stay', 'Continue', 'Place type, dates, funding, and the payment consequence before Continue.', 'careHome'),
        step('send', 'Send', undefined, undefined, 'careHome'),
      ],
    },
    {
      title: 'Rose: claiming for herself',
      steps: [
        step('welcome', 'Welcome', 'Start', 'Every prompt in the second person.', 'herself'),
        step('before', 'What we have', undefined, 'No signer step.', 'herself'),
        step('hub', 'After the voice note', undefined, undefined, 'herself'),
      ],
    },
    {
      title: 'Eileen: an attorney signs',
      steps: [
        step('card-signer', 'Who signs', 'I have Power of Attorney', 'Health and Welfare alone cannot sign; the card explains and continues.', 'attorney'),
        step('card-watch', 'Keeping her safe', 'Continue', undefined, 'attorney'),
        step('hub', 'After the voice note', undefined, undefined, 'attorney'),
      ],
    },
    {
      title: 'Other forms',
      collapsed: true,
      steps: [
        step('handoff-scotland', 'Scotland'),
        step('handoff-ni', 'Northern Ireland'),
        step('handoff-pip', 'Under State Pension age'),
        step('handoff-aa', 'Already gets AA'),
        step('handoff-pip-now', 'Gets PIP or DLA'),
        step('handoff-abroad', 'Lives abroad'),
      ],
    },
    {
      title: 'The Care Adviser’s side',
      screen: 'desktop',
      steps: [
        {
          id: 'adviser',
          title: 'The claim arrives',
          render: () => <AdviserView claim={buildClaim({ prepare: sent })} />,
          // Under the screen: a flow note is drawn a screen and a half wide,
          // which on a desktop row pushes the one screen out of view.
          note: (
            <Note variant='body2'>
              The pathway flags, the start-date deadline, everything the family set aside to talk through, and every answer
              with its source and question number.
            </Note>
          ),
        },
      ],
    },
  ],
  states: [
    ...[
      ['card-night', 'Help at night'],
      ['card-falls', 'Falls and stumbles'],
      ['card-conditions', 'Conditions and medicines'],
      ['card-residence', 'Living in Great Britain'],
      ['card-consent', 'DWP contacting her doctors'],
      ['card-bank', 'Where the money goes'],
      ['card-words', 'The words on the form'],
      ['round-prescription', 'A photo of the prescription'],
      ['round-opening', 'The opening voice note'],
      ['home', 'Carry on later'],
    ].map(([id, title]) => ({ id, title, kind: 'view' as const, render: at(id) })),
  ],
  components: {
    mui: ['Card', 'Chip', 'TextField', 'ToggleButtonGroup', 'Alert', 'MenuItem'],
    existing: ['ChatBubble', 'VoiceNote', 'Attachment', 'KeniOrb', 'ChatTyping', 'AppBar', 'BottomSheet', 'PageHeader'],
    new: [
      // Copies of FactList, ChatActionRow and OptionCard with keyboard and
      // source changes, kept here so the shared ones stay as they are.
      'ClaimFactList',
      'ClaimActionRow',
      'ClaimOptionCard',
      'HubScreen',
      'RoundScreen',
      'BeforeScreen',
      'HandoffScreen',
      'ReviewScreen',
      'SendScreen',
      'AskCard',
      'BatchCheckCard',
      'AdviserView',
      'claim/engine',
      'claim/asks',
      'claim/registry',
    ],
  },
};

